'use client';

import React from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { clearUnsavedChanges } from '@/store/slices/unsavedChangesSlice';
import { hideToolSide } from '@/store/slices/toolSideSlice';
import { resetAll } from '@/features/tool/slices/toolSlice';

const UNSAVED_CHANGES_MESSAGE =
  'Unsaved Changes\n\nYou have unsaved changes. If you leave now, your uploaded file and changes will be lost.';
export const UNSAVED_CHANGES_BYPASS_EVENT = 'unsaved-changes:bypass-next';

export const useUnsavedChangesPrompt = () => {
  const dispatch = useAppDispatch();
  const hasUnsavedChanges = useAppSelector(
    (state) => state.unsavedChanges.isDirty
  );

  const confirmDiscard = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.confirm(UNSAVED_CHANGES_MESSAGE);
  }, []);

  const discardChanges = React.useCallback(() => {
    dispatch(clearUnsavedChanges());
    dispatch(hideToolSide());
    dispatch(resetAll());
  }, [dispatch]);

  const setGlobalBypass = React.useCallback((enabled: boolean) => {
    if (typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(
      new CustomEvent(UNSAVED_CHANGES_BYPASS_EVENT, {
        detail: { enabled },
      })
    );
  }, []);

  const runWithBypass = React.useCallback(
    async (action: () => void | Promise<void>) => {
      setGlobalBypass(true);

      try {
        await action();
      } finally {
        window.setTimeout(() => {
          setGlobalBypass(false);
        }, 0);
      }
    },
    [setGlobalBypass]
  );

  const guardAction = React.useCallback(
    (action: () => void | Promise<void>) => {
      if (!hasUnsavedChanges) {
        void action();
        return false;
      }

      if (!confirmDiscard()) {
        return true;
      }

      discardChanges();
      void runWithBypass(action);
      return false;
    },
    [confirmDiscard, discardChanges, hasUnsavedChanges, runWithBypass]
  );

  const discardChangesAndRun = React.useCallback(
    (action: () => void | Promise<void>) => {
      if (!hasUnsavedChanges) {
        void action();
        return false;
      }

      if (!confirmDiscard()) {
        return true;
      }

      discardChanges();
      void runWithBypass(action);
      return false;
    },
    [confirmDiscard, discardChanges, hasUnsavedChanges, runWithBypass]
  );

  return {
    hasUnsavedChanges,
    confirmDiscard,
    discardChanges,
    guardAction,
    discardChangesAndRun,
  };
};
